import {
  getCompanyNameValidationError,
  toPrimoUpSlug,
} from "./companyIdentifiers.ts";

export type CompanyCreationField =
  | "nome"
  | "indirizzo"
  | "citta"
  | "cap"
  | "provincia"
  | "partita_iva"
  | "codice_fiscale"
  | "telefono"
  | "email"
  | "pec"
  | "sdi"
  | "customer_type_key"
  | "suffix"
  | "slug";

export type CompanyCreationValidationInput = Partial<
  Record<CompanyCreationField, string>
>;

export type CompanyCreationFieldErrors = Partial<
  Record<CompanyCreationField, string>
>;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isDuplicateCompanySlugError(message: unknown): boolean {
  if (typeof message !== "string") return false;

  const normalizedMessage = message.trim().toLocaleLowerCase("it-IT");
  return (
    normalizedMessage.includes("slug") &&
    (normalizedMessage.includes("esiste già") ||
      normalizedMessage.includes("già utilizzato") ||
      normalizedMessage.includes("already exists") ||
      normalizedMessage.includes("already in use"))
  );
}

/**
 * Validazione sincrona condivisibile del contratto di creazione società.
 *
 * Tutti i campi mostrati dal form sono obbligatori soltanto in creazione. Gli
 * aggiornamenti delle società legacy continuano a usare il percorso separato
 * `updateCompany`, che accetta ancora modifiche parziali.
 */
export function getRequiredCompanyCreationFieldErrors(
  input: CompanyCreationValidationInput,
): CompanyCreationFieldErrors {
  const errors: CompanyCreationFieldErrors = {};
  const value = (field: CompanyCreationField) => input[field]?.trim() ?? "";

  const companyNameError = getCompanyNameValidationError(value("nome"));
  if (companyNameError) errors.nome = companyNameError;

  if (!value("indirizzo")) errors.indirizzo = "Indirizzo è obbligatorio";
  if (!value("citta")) errors.citta = "Città è obbligatoria";

  const zip = value("cap");
  if (!zip) {
    errors.cap = "CAP è obbligatorio";
  } else if (!/^\d{5}$/.test(zip)) {
    errors.cap = "CAP deve essere composto da 5 cifre";
  }

  const province = value("provincia").toUpperCase();
  if (!province) {
    errors.provincia = "Provincia è obbligatoria";
  } else if (!/^[A-Z]{2}$/.test(province)) {
    errors.provincia = "Provincia deve essere composta da 2 lettere";
  }

  const vat = value("partita_iva");
  if (!vat) {
    errors.partita_iva = "Partita IVA è obbligatoria";
  } else if (!/^\d{11}$/.test(vat)) {
    errors.partita_iva = "Partita IVA deve essere composta da 11 cifre";
  }

  const taxCode = value("codice_fiscale").toUpperCase();
  if (!taxCode) {
    errors.codice_fiscale = "Codice fiscale è obbligatorio";
  } else if (!/^[A-Z0-9]{11,16}$/.test(taxCode)) {
    errors.codice_fiscale = "Codice fiscale non valido";
  }

  if (!value("telefono")) errors.telefono = "Telefono è obbligatorio";

  const email = value("email");
  if (!email) {
    errors.email = "Email è obbligatoria";
  } else if (!EMAIL_PATTERN.test(email)) {
    errors.email = "Email non valida";
  }

  const certifiedEmail = value("pec");
  if (!certifiedEmail) {
    errors.pec = "PEC è obbligatoria";
  } else if (!EMAIL_PATTERN.test(certifiedEmail)) {
    errors.pec = "PEC non valida";
  }

  const recipientCode = value("sdi").toUpperCase();
  if (!recipientCode) {
    errors.sdi = "Codice SDI è obbligatorio";
  } else if (!/^[A-Z0-9]{7}$/.test(recipientCode)) {
    errors.sdi = "Codice SDI deve essere composto da 7 lettere o numeri";
  }

  if (!value("customer_type_key")) {
    errors.customer_type_key = "Tipologia cliente è obbligatoria";
  }

  const suffix = value("suffix").toUpperCase();
  if (!suffix) {
    errors.suffix = "Suffisso è obbligatorio";
  } else if (!/^[A-Z0-9]{2,5}$/.test(suffix)) {
    errors.suffix =
      "Suffisso deve essere composto da 2-5 lettere maiuscole o numeri";
  }

  if (!toPrimoUpSlug(value("slug"))) {
    errors.slug = "Slug PrimoUp non valido";
  }

  return errors;
}
